import { createTheme, Theme } from '@mui/material/styles';
import { config } from '../config';

export const lightTheme: Theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#4caf50',
      light: '#66bb6a',
      dark: '#388e3c',
    },
    error: {
      main: '#f44336',
    },
    warning: {
      main: '#ff9800',
    },
    success: {
      main: '#4caf50',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

export const darkTheme: Theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#42a5f5',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    secondary: {
      main: '#66bb6a',
      light: '#81c784',
      dark: '#4caf50',
    },
    error: {
      main: '#ef5350',
    },
    warning: {
      main: '#ffa726',
    },
    success: {
      main: '#66bb6a',
    },
    background: {
      default: '#1e1e1e',
      paper: '#2d2d2d',
    },
    text: {
      primary: '#e0e0e0',
      secondary: '#9e9e9e',
    },
  },
  typography: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
});

/**
 * The basemap tile template, for both themes (#574).
 *
 * No `isDarkMode` parameter any more, and that is the fix rather than a tidy-up.
 * Dark mode used to fetch CARTO `dark_all` while light mode fetched OSM — two
 * providers, one attribution string, and no way to notice they had drifted
 * apart. CARTO then started requiring an API key and stamped
 * `API KEY REQUIRED` across every dark tile. The code had not changed; the
 * terms had, so no commit could have gone red.
 *
 * One provider now serves both themes and dark is produced by inverting it
 * (`getBasemapThemeClassName`), so the attribution cannot describe a source the
 * map is not using. Which provider is a deployment choice — see
 * `config.mapTileUrl`, which a cluster can repoint without a rebuild.
 */
export const getMapTileUrl = (): string => config.mapTileUrl;

/**
 * CSS class carrying the basemap theme, for the **wrapper element around
 * `<MapContainer>`** — never on `<TileLayer>` or `<MapContainer>` themselves.
 *
 * The basemap is light in both themes (#574), so dark mode is produced here:
 * the `.basemap-dark` rule in globals.css inverts the tile pane and rotates the
 * hue back, which turns OSM's paper-white map into a dark one without fetching
 * a second, differently-licensed source (#344, #432). Light mode gets no class,
 * so the tiles render untouched.
 *
 * Returns no class when `config.mapTilesAreDark` says the configured provider
 * is already dark — inverting a dark basemap produces a light one, which is a
 * white map in dark mode.
 *
 * ## Why the wrapper, and not the map
 *
 * This used to return `dark-basemap` for `<TileLayer className>`, which is
 * **write-once**: react-leaflet's `updateTileLayer` only ever diffs `url`,
 * `opacity` and `zIndex`, so switching to light mode swapped the tiles and left
 * the dark filter attached. A ×4.6 brightness on light OSM tiles clamps ~91% of
 * their pixels to pure white, which is why the map went blank (#440).
 *
 * `<MapContainer className>` is no better — it does `useState({ className, id,
 * style })`, capturing the value on first render and never updating it. The
 * fix suggested in #440 would have failed the same way.
 *
 * A plain wrapper element is the one place React genuinely re-renders, so the
 * class cannot outlive the theme. `mapThemeClassNames` in
 * `styles/mapTheme.test.ts` guards against the class drifting back onto a
 * write-once prop.
 */
export const getBasemapThemeClassName = (isDarkMode: boolean): string =>
  isDarkMode && !config.mapTilesAreDark ? 'basemap-dark' : '';

/**
 * Attribution for whatever `getMapTileUrl` is actually serving (#574).
 *
 * This returned OpenStreetMap's credit unconditionally, including in dark mode,
 * where the tiles came from CARTO — whose attribution was never shown at all.
 * A licensing problem, not a cosmetic one. Both now come from the same
 * configuration, so repointing the basemap repoints the credit with it.
 */
export const getMapAttribution = (): string => config.mapAttribution;
